import React from 'react';

import Icon from 'views/site/Icon.js';

import API from 'files/api.js';
import Catalog from 'files/Catalog.js';
import Theme from 'styles/Theme.js';
import Utils from 'files/Utils.js';

/*
 * Footer
 *
 * Replaces the dealership colored slab in managers/Components.js. Everything
 * that slab carried for compliance reasons is carried here too — the BBB seal,
 * the dealership's own disclaimer text, the dealer login, and the design
 * credit. None of it is decoration and none of it should be dropped without
 * asking whoever asked for it in the first place.
 *
 * The ground is the storefront's near-black rather than the dealership's
 * color. A footer is a large field, and painting one in an arbitrary
 * author-supplied color is where the brand palette most often falls apart;
 * the dealership's color appears on the callback control instead.
 */

const Footer = ({ dealership, onNavigate }) => {

    const onItemClick = item => {
        if(typeof(item.onClick) === 'function') {
            item.onClick();
            return;
        }
        if(onNavigate) {
            onNavigate(item);
        }
    }

    const getColumns = () => {
        return [{
            items: [
                { key: 'home', title: 'Home', view: 'home', waypoint: 'home' },
                { key: 'products', title: 'Products', view: 'products', visible: Catalog.isVisible(dealership) },
                { key: 'about', title: 'About', view: 'about' },
                { key: 'careers', title: 'Careers', view: 'careers', visible: dealership?.recruiting_survey ? true : false }
            ],
            title: 'Company'
        },{
            /*
             * Each of these lands on the thing it names rather than on the top
             * of the support page. Guides and manuals scroll to their section;
             * asking a question opens the guided flow. See SupportRoutes in
             * App.js.
             */
            items: [
                { key: 'support', title: 'Guides and troubleshooting', view: 'guides' },
                { key: 'support', title: 'Product manuals', view: 'product_manuals' },
                { key: 'support', title: 'Ask a question', view: 'ask_a_question' }
            ],
            title: 'Support'
        },{
            // Its own column rather than the tail of Support. These came out of
            // the header navigation, and they are what a reader scans a footer
            // for — mixing them into support links makes them harder to find,
            // not easier.
            items: [
                { key: 'privacy', title: 'Privacy Policy', view: 'privacy' },
                { key: 'terms_and_conditions', title: 'Terms and Conditions', view: 'terms_and_conditions' }
            ],
            title: 'Legal'
        }];
    }

    const getContactColumn = () => {
        return (
            <div>
                <span style={{
                    color: Theme.colors.white,
                    display: 'block',
                    fontFamily: Theme.fonts.body,
                    fontSize: 14,
                    fontWeight: 700,
                    marginBottom: 20
                }}>{'Contact Us'}</span>
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 12
                }}>
                    {dealership.phone_number && (
                        <span
                        className={'text-button'}
                        onClick={window.open.bind(this, `tel://${dealership.phone_number}`)}
                        style={Theme.textStyles.body({ color: 'rgba(255,255,255,0.6)', fontSize: 14 })}>
                            {Utils.formatPhoneNumber(dealership.phone_number)}
                        </span>
                    )}
                    {dealership.email_address && (
                        <span
                        className={'text-button'}
                        onClick={window.open.bind(this, `mailto:${dealership.email_address}`)}
                        style={Theme.textStyles.body({ color: 'rgba(255,255,255,0.6)', fontSize: 14 })}>
                            {dealership.email_address}
                        </span>
                    )}
                    <span
                    className={'text-button'}
                    onClick={onItemClick.bind(this, { key: 'contact', view: 'contact' })}
                    style={{
                        alignItems: 'center',
                        border: `1px solid white`,
                        borderRadius: 20,
                        color: 'white',
                        display: 'inline-flex',
                        fontFamily: Theme.fonts.body,
                        fontSize: 13,
                        fontWeight: 600,
                        gap: 8,
                        marginTop: 8,
                        padding: '10px 20px',
                        width: 'fit-content'
                    }}>
                        {'Request a Callback'}
                        <Icon name={'arrow-right'} size={12} />
                    </span>
                </div>
            </div>
        )
    }

    if(!dealership) {
        return null;
    }

    return (
        <footer
        className={'site'}
        style={{
            backgroundColor: Theme.colors.dark(),
            borderTop: `2px solid ${Theme.colors.white}`, 
            color: Theme.colors.white,
            paddingTop: Utils.isMobile() ? 40 : 60,
            width: '100%'
        }}>
            <div style={{
                margin: '0 auto',
                maxWidth: Theme.layout.maxWidth,
                padding: Utils.isMobile() ? '0 20px' : '0 40px'
            }}>
                <div style={{
                    display: 'grid',
                    // brand · company · support · legal · contact
                    gap: Utils.isMobile() ? 40 : 48,
                    gridTemplateColumns: Utils.isMobile() ? '1fr' : '1.4fr 1fr 1fr 1fr 1.4fr',
                    paddingBottom: 40
                }}>
                    <div>
                        <div style={{
                            alignItems: 'center',
                            display: 'flex',
                            gap: 12,
                            marginBottom: 16
                        }}>
                            <img
                            alt={dealership.name}
                            src={dealership.logo?.url || 'images/dealership-replicated-logo-placeholder.png'}
                            style={{
                                borderRadius: 8,
                                height: 40,
                                objectFit: 'contain',
                                width: 40
                            }} />
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column'
                            }}>
                                <span style={{
                                    color: Theme.colors.white,
                                    fontFamily: Theme.fonts.body,
                                    fontSize: 15,
                                    fontWeight: 700,
                                    lineHeight: 1.3
                                }}>{dealership.name}</span>
                                {dealership.locality && dealership.administrative_area_level_1 && (
                                    <span style={Theme.textStyles.caption({ color: 'rgba(255,255,255,0.45)' })}>
                                        {`${dealership.locality}, ${dealership.administrative_area_level_1}`}
                                    </span>
                                )}
                            </div>
                        </div>

                        {typeof(dealership.bbb_url) === 'string' && (
                            <img
                            alt={`${dealership.name} BBB Business Review`}
                            className={'text-button'}
                            onClick={window.open.bind(this, `${dealership.bbb_url}/#sealclick`)}
                            src={'images/bbb-badge.png'}
                            style={{
                                border: 0,
                                height: 45,
                                objectFit: 'contain',
                                width: 'auto'
                            }} />
                        )}
                    </div>

                    {getColumns().map((column, index) => (
                        <div key={index}>
                            <span style={{
                                color: Theme.colors.white,
                                display: 'block',
                                fontFamily: Theme.fonts.body,
                                fontSize: 14,
                                fontWeight: 700,
                                marginBottom: 20
                            }}>{column.title}</span>
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 12
                            }}>
                                {column.items.filter(item => {
                                    return item.visible !== false;
                                }).map((item, itemIndex) => (
                                    <span
                                    className={'text-button'}
                                    key={itemIndex}
                                    onClick={onItemClick.bind(this, item)}
                                    style={Theme.textStyles.body({ color: 'rgba(255,255,255,0.6)', fontSize: 14 })}>
                                        {item.title}
                                    </span>
                                ))}
                            </div>
                        </div>
                    ))}

                    {getContactColumn()}
                </div>

                {/* The dealership's own disclaimer. Supplied per dealership and
                    required by some of them, so it is rendered verbatim. */}
                {typeof(dealership.disclaimer_text) === 'string' && (
                    <div style={{
                        borderTop: '1px solid rgba(255,255,255,0.1)',
                        padding: '20px 0'
                    }}>
                        <span style={Theme.textStyles.caption({
                            color: 'rgba(255,255,255,0.4)',
                            display: 'block',
                            lineHeight: 1.6
                        })}>{dealership.disclaimer_text}</span>
                    </div>
                )}

                <div style={{
                    alignItems: Utils.isMobile() ? 'flex-start' : 'center',
                    borderTop: '1px solid rgba(255,255,255,0.1)',
                    display: 'flex',
                    flexDirection: Utils.isMobile() ? 'column' : 'row',
                    gap: 12,
                    justifyContent: 'space-between',
                    padding: '20px 0'
                }}>
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2
                    }}>
                        <span style={Theme.textStyles.caption({ color: 'rgba(255,255,255,0.4)' })}>
                            {`Copyright © ${new Date().getFullYear()} ${dealership.name}. All Rights Reserved.`}
                        </span>
                        <span style={Theme.textStyles.caption({ color: 'rgba(255,255,255,0.25)', fontSize: 11 })}>
                            {`Version ${API.app_version} Build ${API.build}`}
                        </span>
                    </div>

                    <div style={{
                        alignItems: Utils.isMobile() ? 'flex-start' : 'flex-end',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 6
                    }}>
                        <span
                        className={'text-button'}
                        onClick={window.open.bind(this, 'https://homesafealerts.com/dealers')}
                        style={Theme.textStyles.caption({
                            color: 'rgba(255,255,255,0.6)',
                            textDecoration: 'underline'
                        })}>{'Dealer Login'}</span>
                        <span style={Theme.textStyles.caption({ color: 'rgba(255,255,255,0.4)' })}>
                            {'Website Designed by '}
                            <a
                            className={'text-button'}
                            href={`https://duralabs.io/?utm_source=ghs_replicated_website&utm_dealership_id=${dealership.id}`}
                            target={'_blank'}
                            style={{ textDecoration: 'underline' }}>{'Dura Labs LLC'}</a>
                        </span>
                    </div>
                </div>
            </div>
        </footer>
    )
}

export default Footer;